import { BUTLER_JWT_TOKEN_KEY } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiOrderStatus, useOrderStatusStore } from '@hooks-api/butler/useApiOrderStatus'
import { useCreateOrderFromPdf } from '@hooks-api/butler/useCreateOrderFromPDF'
import { OrderStatus } from '@hooks-api/types/dataModel/butler'
import Cookies from 'js-cookie'
import { type ButlerTabType } from '../../constants/butler-tab'
import { type ButlerErrors, type FpB2bCartValues } from '../../types'
import { cartStoreMap, type PlatformType } from './store-map'

export const useFpB2bButlerService = (campaignId: number, platform: PlatformType) => {
  const { useCartStore, cartDefaultValues } = cartStoreMap[platform]
  const [cartValues, setCartValues] = useCartStore(campaignId)
  const { retryOrderStatus, startRetryTime, error: orderStatusErrorFromStore, reset: resetOrderStatus } = useOrderStatusStore()

  // 只有在有 Butler token 時才發送 order status 請求，避免剛登入還沒拿到token時的 401 錯誤
  const butlerToken = Cookies.get(BUTLER_JWT_TOKEN_KEY)
  const shouldFetchOrderStatus = butlerToken && cartValues.butler.orderId

  const { destructuredData: orderStatus, error: orderStatusError } = useApiOrderStatus(
    { orderId: shouldFetchOrderStatus ? cartValues.butler.orderId : null },
    {
      onSuccess: () => {
        retryOrderStatus(cartValues.butler.orderId)
      },
    },
  )
  const { trigger: createOrderFromPdfApi, isMutating: isCreateOrderFromPdfLoading } = useCreateOrderFromPdf()

  const setDraftName = useCallbackEvent((draftName: string) => {
    setCartValues(state => ({
      ...state,
      butler: {
        ...state.butler,
        draftName,
      },
    }))
  })

  const setOrderId = useCallbackEvent((orderId: string) => {
    setCartValues(state => ({
      ...state,
      butler: {
        ...state.butler,
        orderId,
      },
    }))
  })

  const setIsImported = useCallbackEvent((isImported: boolean) => {
    setCartValues(state => ({
      ...state,
      butler: {
        ...state.butler,
        isImported,
      },
    }))
  })

  const setButlerErrors = useCallbackEvent((errors: ButlerErrors) => {
    setCartValues(state => ({
      ...state,
      butler: {
        ...state.butler,
        errors: {
          ...state.butler.errors,
          ...errors,
        },
      },
    }))
  })

  const setSelectedTab = useCallbackEvent((selectedTab: ButlerTabType) => {
    setCartValues(state => ({
      ...state,
      butler: {
        ...state.butler,
        selectedTab,
      },
    }))
  })

  const resetButler = useCallbackEvent((defaultValues: Partial<FpB2bCartValues['butler']>) => {
    setCartValues(state => ({
      ...state,
      butler: {
        ...cartDefaultValues.butler,
        ...defaultValues,
      },
    }))
  })

  const createOrderFromPdf = async ({ file, customerId }: { file: File, customerId: number }) => {
    const res = await createOrderFromPdfApi({
      file,
      campaignId,
      customerId,
    })
    const orderId = res.data.data.orderId
    if (!orderId) {
      throw new Error('Create order failed, order id not found.')
    }
    return orderId
  }

  const isGetOrderStatusFailed = orderStatusError !== undefined || orderStatusErrorFromStore !== null
  const isOrderStatusIncorrectFormat = orderStatus?.status === OrderStatus.INCORRECT_FORMAT
  const isOrderStatusInProgress = !isGetOrderStatusFailed && orderStatus?.status === OrderStatus.IN_PROGRESS
  const isOrderStatusCompleted = orderStatus?.status === OrderStatus.COMPLETED

  return {
    ...cartValues.butler,
    isCreateOrderFromPdfLoading,
    isOrderStatusInProgress,
    isOrderStatusCompleted,
    isGetOrderStatusFailed,
    isOrderStatusIncorrectFormat,
    startRetryTime,
    setDraftName,
    setOrderId,
    setIsImported,
    setButlerErrors,
    setSelectedTab,
    resetButler,
    resetOrderStatus,
    createOrderFromPdf,
  }
}
